/**
 * Canonical primary navigation links. Single source of truth — import from
 * here instead of hardcoding the same hrefs/labels in the header, footer, and
 * dock (they had drifted and had to be edited in four places).
 *
 * Each surface composes its own extra links around this shared set:
 * - Header (mobile + desktop): renders the visible primary links as-is.
 * - Footer: appends Contact + Privacy Policy + Photos.
 * - AppDock: uses a subset (Craft, Projects, Stack) plus Home/Back and socials.
 */

export type NavLink = {
  href: string
  label: string
  /**
   * Intentionally hidden links. Kept in this list rather than deleted so the
   * full nav stays visible in one place; `false` means not rendered anywhere.
   */
  enabled?: boolean
}

// Order matters. Disabled entries sit at their original interleaved positions.
export const PRIMARY_NAV: readonly NavLink[] = [
  { href: '/about', label: 'About' },
  { href: '/articles', label: 'Articles', enabled: false },
  { href: '/projects', label: 'Projects' },
  { href: '/speaking', label: 'Speaking', enabled: false },
  { href: '/craft', label: 'Craft' },
  { href: '/stack', label: 'Stack' },
  { href: '/blog', label: 'Blog', enabled: false },
]

// The links that actually render: About, Projects, Craft, Stack.
export const PRIMARY_NAV_VISIBLE: readonly NavLink[] = PRIMARY_NAV.filter(
  (link) => link.enabled !== false,
)

// Lookup by href for surfaces (e.g. the dock) that show a reordered subset.
export const PRIMARY_NAV_BY_HREF: Record<string, NavLink> = Object.fromEntries(
  PRIMARY_NAV.map((link) => [link.href, link]),
)
